import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Plus, Search, Edit3, Trash2, X,
    Activity, Shield, Users,
    ChevronRight, CheckCircle2, Circle, RefreshCw
} from 'lucide-react';
import {
    getNotificationSettingsRequest,
    createNotificationSettingRequest,
    updateNotificationSettingRequest,
    deleteNotificationSettingRequest
} from '../../../../redux/actions/notificationSettingsActions';
import { getAllModulesRequest } from '../../../../redux/actions/moduleActions';
import { getActiveRolesRequest } from '../../../../redux/actions/roleActions';
import { getAllUsersRequest } from '../../../../redux/actions/userActions';
import { getAllEmailTemplatesRequest } from '../../../../redux/actions/emailActions';
import Select from 'react-select';
import Swal from 'sweetalert2';
import './NotificationSettings.css';
import ScrollToTop from '../../../../Ui/UiJs/ScrollToTop';

const TARGET_CONFIG = {
    DYNAMIC: {
        label: 'Destinataire dynamique',
        description: "Sélectionné dynamiquement par le service métier",
        class: 'ns-badge-blue', cardClass: 'ns-target-card-blue', icon: Activity
    },
    SPECIFIC_ROLE: {
        label: 'Rôle spécifique',
        description: 'Tous les utilisateurs ayant un ou plusieurs rôles donnés',
        class: 'ns-badge-orange', cardClass: 'ns-target-card-orange', icon: Shield
    },
    SPECIFIC_USER: {
        label: 'Utilisateur(s)',
        description: 'Sélection manuelle d\'un ou plusieurs utilisateurs',
        class: 'ns-badge-green', cardClass: 'ns-target-card-green', icon: Users
    },
};

const EMPTY_FORM = {
    eventCode: '', eventName: '', inAppEnabled: true, emailEnabled: false, emailTemplateId: '',
    inAppTitleTemplate: '', inAppBodyTemplate: '',
    routePath: '', icon: 'bell',
    targetTypes: ['DYNAMIC'],
    moduleId: '', targetRoleIds: [], targetUserIds: [],
    priority: 'INFO', isActive: true
};

const NotificationSettings = () => {
    const dispatch = useDispatch();
    const { settings, loading } = useSelector(state => state.notificationSettings || { settings: [], loading: false });
    const { modules } = useSelector(state => state.module || { modules: [] });
    const { roles } = useSelector(state => state.role || { roles: [] });
    const { users } = useSelector(state => state.user || { users: [] });
    const { templates: emailTemplates = [] } = useSelector(state => state.emails || { templates: [] });

    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSetting, setEditingSetting] = useState(null);
    const [modalStep, setModalStep] = useState(1);
    const [formData, setFormData] = useState(EMPTY_FORM);

    const refreshData = () => {
        dispatch(getNotificationSettingsRequest());
        dispatch(getAllModulesRequest());
        dispatch(getActiveRolesRequest());
        dispatch(getAllUsersRequest());
        dispatch(getAllEmailTemplatesRequest());
    };

    useEffect(() => {
        refreshData();
    }, [dispatch]);

    const filtered = useMemo(() => (settings || []).filter(s => {
        const matchesQ = !q ||
            s.eventCode.toLowerCase().includes(q.toLowerCase()) ||
            s.inAppTitleTemplate.toLowerCase().includes(q.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' ||
            (statusFilter === 'ACTIVE' && s.isActive) ||
            (statusFilter === 'INACTIVE' && !s.isActive);
        return matchesQ && matchesStatus;
    }), [settings, q, statusFilter]);

    const handleOpenModal = (setting = null) => {
        if (setting) {
            setEditingSetting(setting);
            setFormData({
                eventCode: setting.eventCode,
                eventName: setting.eventName || '',
                inAppEnabled: setting.inAppEnabled,
                emailEnabled: setting.emailEnabled || false,
                emailTemplateId: setting.emailTemplateId || '',
                inAppTitleTemplate: setting.inAppTitleTemplate,
                inAppBodyTemplate: setting.inAppBodyTemplate,
                routePath: setting.routePath || '',
                icon: setting.icon || '',
                targetTypes: setting.targetTypes || (setting.targetType ? [setting.targetType] : ['DYNAMIC']),
                moduleId: setting.moduleId || '',
                targetRoleIds: setting.targetRoles?.map(r => r.id) || [],
                targetUserIds: setting.targetUsers?.map(u => u.id) || [],
                priority: setting.priority || 'INFO',
                isActive: setting.isActive
            });
        } else {
            setEditingSetting(null);
            setFormData(EMPTY_FORM);
        }
        setModalStep(1);
        setIsModalOpen(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingSetting) dispatch(updateNotificationSettingRequest(editingSetting.id, formData));
        else dispatch(createNotificationSettingRequest(formData));
        setIsModalOpen(false);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Supprimer la règle ?',
            text: "Cette action désactivera les notifications pour cet événement.",
            icon: 'warning', showCancelButton: true,
            confirmButtonColor: '#2a5ca9', cancelButtonColor: '#9ca3af',
            confirmButtonText: 'Oui, supprimer', cancelButtonText: 'Annuler'
        }).then(r => { if (r.isConfirmed) dispatch(deleteNotificationSettingRequest(id)); });
    };

    const handleToggleActive = (setting) => {
        dispatch(updateNotificationSettingRequest(setting.id, {
            ...setting,
            targetTypes: setting.targetTypes || (setting.targetType ? [setting.targetType] : ['DYNAMIC']),
            targetRoleIds: setting.targetRoles?.map(r => r.id) || [],
            targetUserIds: setting.targetUsers?.map(u => u.id) || [],
            isActive: !setting.isActive
        }));
    };

    const toggleTargetType = (key) => {
        const current = formData.targetTypes;
        const next = current.includes(key) ? current.filter(t => t !== key) : [...current, key];
        const stillNeedsRoles = next.some(t => ['SPECIFIC_ROLE', 'SPECIFIC_USER'].includes(t));
        setFormData({ ...formData, targetTypes: next, targetRoleIds: stillNeedsRoles ? formData.targetRoleIds : [], targetUserIds: stillNeedsRoles ? formData.targetUserIds : [] });
    };

    const toggleRole = (id) => {
        const next = formData.targetRoleIds.includes(id) ? formData.targetRoleIds.filter(x => x !== id) : [...formData.targetRoleIds, id];
        setFormData({ ...formData, targetRoleIds: next, targetUserIds: [] });
    };

    const needsRolePicker = formData.targetTypes.some(t => ['SPECIFIC_ROLE', 'SPECIFIC_USER'].includes(t));
    const needsUserPicker = formData.targetTypes.some(t => ['SPECIFIC_ROLE', 'SPECIFIC_USER'].includes(t));

    const availableRoles = useMemo(() => formData.moduleId ? roles.filter(r => r.moduleId === parseInt(formData.moduleId)) : roles, [roles, formData.moduleId]);
    const availableUsersForSelect = useMemo(() => {
        let filtered = users;
        if (formData.moduleId) filtered = filtered.filter(u => u.roles?.some(r => r.moduleId === parseInt(formData.moduleId)));
        if (formData.targetRoleIds?.length > 0) filtered = filtered.filter(u => u.roles?.some(r => formData.targetRoleIds.includes(r.id)));
        return filtered.map(u => ({ value: u.id, label: u.fullName }));
    }, [users, formData.moduleId, formData.targetRoleIds]);

    const step2Complete = formData.targetTypes.length > 0 && (formData.targetTypes.every(t => t === 'DYNAMIC') || (formData.targetTypes.includes('SPECIFIC_ROLE') && formData.targetRoleIds.length > 0) || (formData.targetTypes.includes('SPECIFIC_USER') && formData.targetUserIds.length > 0));

    const renderTargetBadges = (setting) => {
        const types = setting.targetTypes || (setting.targetType ? [setting.targetType] : []);
        return (
            <div className="ns-badge-list">
                {types.map(type => {
                    const config = TARGET_CONFIG[type];
                    const Icon = config?.icon || Activity;
                    return (
                        <span key={type} className={`ns-badge ${config?.class || 'ns-badge-blue'}`}>
                            <Icon size={11} className="me-1" />
                            {config?.label || type}
                            {type === 'SPECIFIC_ROLE' && setting.targetRoles?.length > 0 && <span className="ns-target-sub"> · {setting.targetRoles.length} rôles</span>}
                            {type === 'SPECIFIC_USER' && setting.targetUsers?.length > 0 && <span className="ns-target-sub"> · {setting.targetUsers.length} util.</span>}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="ns-page">
            {/* ── Header ─────────────────────────── */}
            <div className="ns-header">
                <div>
                    <h2>Paramétrage des notifications</h2>
                    <p className="ns-subtitle">Gérez les règles d'envoi et les templates dynamiques de la plateforme.</p>
                </div>
                <div className="application-logs-header-actions">
                    <button className="ns-btn ns-btn-primary" onClick={() => handleOpenModal()}>
                        <Plus size={14} /> Nouvelle Règle
                    </button>
                    <button className="ns-btn ns-btn-outline" onClick={refreshData} disabled={loading}>
                        <RefreshCw size={14} className={loading ? "al-spin" : ""} />
                    </button>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────── */}
            <div className="ns-toolbar">
                <div className="ns-search-wrapper">
                    <Search className="ns-search-icon" size={14} />
                    <input type="text" className="ns-search-input"
                           placeholder="Rechercher par événement, titre..."
                           value={q} onChange={e => setQ(e.target.value)} />
                </div>
                <div className="ns-filters">
                    {['ALL', 'ACTIVE', 'INACTIVE'].map(f => (
                        <button key={f} className={`ns-filter-tab ${statusFilter === f ? 'active' : ''}`}
                                onClick={() => setStatusFilter(f)}>
                            {f === 'ALL' ? 'Tous' : f === 'ACTIVE' ? 'Actifs' : 'Inactifs'}
                        </button>
                    ))}
                </div>
                <div className="ns-count">
                    {filtered.length} règle{filtered.length > 1 ? 's' : ''}
                </div>
            </div>

            {/* ── Table ──────────────────────────── */}
            <div className="ns-table-wrapper">
                {loading ? (
                    <div className="al-loading">
                        <div className="al-spinner" />
                        <span>Chargement...</span>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="al-empty">Aucune règle de notification trouvée.</div>
                ) : (
                    <table className="ns-table">
                        <thead>
                        <tr>
                            <th>Événement</th>
                            <th>Cible(s)</th>
                            <th>Module</th>
                            <th>Canaux</th>
                            <th>Statut</th>
                            <th className="text-end">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.map(setting => (
                            <tr key={setting.id}>
                                <td>
                                    <div className="ns-icon-cell">
                                        <div className="ns-avatar"><Activity size={16} /></div>
                                        <div>
                                            <div style={{ fontWeight: 700, color: '#1a3b6e', fontSize: '12px' }}>{setting.eventCode}</div>
                                            <div style={{ color: '#6b7280', fontSize: '11px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{setting.inAppTitleTemplate}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>{renderTargetBadges(setting)}</td>
                                <td><span style={{ color: '#6e91c1', fontWeight: 600, fontSize: '11px' }}>{setting.moduleName || 'Global'}</span></td>
                                <td>
                                    {setting.inAppEnabled && <span className="ns-badge ns-badge-blue">In-App</span>}
                                    {setting.emailEnabled && <span className="ns-badge ns-badge-green ms-1">E-mail</span>}
                                </td>
                                <td>
                                    <div className={`ns-toggle ${setting.isActive ? 'on' : ''}`} onClick={() => handleToggleActive(setting)}>
                                        <div className="ns-toggle-box"><div className="ns-toggle-knob" /></div>
                                        <span className="ns-toggle-label">{setting.isActive ? 'Actif' : 'Inactif'}</span>
                                    </div>
                                </td>
                                <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                        <button className="ns-btn ns-btn-outline" style={{ padding: '6px' }} onClick={() => handleOpenModal(setting)}>
                                            <Edit3 size={14} style={{ color: '#2a5ca9' }} />
                                        </button>
                                        <button className="ns-btn ns-btn-outline" style={{ padding: '6px' }} onClick={() => handleDelete(setting.id)}>
                                            <Trash2 size={14} style={{ color: '#ee645f' }} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ===== MODAL ===== */}
            {isModalOpen && (
                <div className="ns-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="ns-modal" onClick={e => e.stopPropagation()}>
                        <div className="ns-modal-header">
                            <div className="ns-modal-title-group">
                                <h3>{editingSetting ? 'Modifier la Règle' : 'Nouvelle Règle'}</h3>
                                <div className="ns-steps">
                                    <button type="button" className={`ns-step ${modalStep === 1 ? 'active' : 'done'}`} onClick={() => setModalStep(1)}>
                                        {modalStep > 1 ? <CheckCircle2 size={14} /> : <span>1</span>}
                                        <span>Template</span>
                                    </button>
                                    <ChevronRight size={14} className="ts-step-sep" />
                                    <button type="button" className={`ns-step ${modalStep === 2 ? 'active' : ''} ${step2Complete && modalStep !== 2 ? 'done' : ''}`} onClick={() => setModalStep(2)}>
                                        {step2Complete && modalStep !== 2 ? <CheckCircle2 size={14} /> : <span>2</span>}
                                        <span>Destinataires</span>
                                    </button>
                                </div>
                            </div>
                            <button className="ns-modal-close" onClick={() => setIsModalOpen(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="ns-modal-form">
                            <div className="ns-modal-body">
                                {modalStep === 1 && (
                                    <div className="ns-form-grid">
                                        <div className="ns-form-group">
                                            <label>Code Événement *</label>
                                            <input type="text" className="ns-input" value={formData.eventCode} onChange={e => setFormData({ ...formData, eventCode: e.target.value })} placeholder="ex: NEW_ABSENCE" required />
                                        </div>
                                        <div className="ns-form-group">
                                            <label>Nom de l'événement *</label>
                                            <input type="text" className="ns-input" value={formData.eventName} onChange={e => setFormData({ ...formData, eventName: e.target.value })} placeholder="ex: Nouvelle absence" required />
                                        </div>
                                        <div className="ns-form-group">
                                            <label>Icône</label>
                                            <input type="text" className="ns-input" value={formData.icon} onChange={e => setFormData({ ...formData, icon: e.target.value })} placeholder="ex: bell" />
                                        </div>
                                        <div className="ns-form-group">
                                            <label>Route (URL)</label>
                                            <input type="text" className="ns-input" value={formData.routePath} onChange={e => setFormData({ ...formData, routePath: e.target.value })} placeholder="ex: /absences" />
                                        </div>
                                        <div className="ns-form-group full">
                                            <label>Titre (Template) *</label>
                                            <input type="text" className="ns-input" value={formData.inAppTitleTemplate} onChange={e => setFormData({ ...formData, inAppTitleTemplate: e.target.value })} placeholder="Nouveau message de {{user}}" required />
                                        </div>
                                        <div className="ns-form-group full">
                                            <label>Message (Template) *</label>
                                            <textarea className="ns-textarea" rows="3" value={formData.inAppBodyTemplate} onChange={e => setFormData({ ...formData, inAppBodyTemplate: e.target.value })} required />
                                        </div>
                                        <div className="ns-form-group full">
                                            <label className="d-flex align-items-center gap-2">
                                                <input type="checkbox" checked={formData.emailEnabled}
                                                       onChange={e => setFormData({ ...formData, emailEnabled: e.target.checked, emailTemplateId: e.target.checked ? formData.emailTemplateId : '' })} />
                                                Envoyer aussi par e-mail
                                            </label>
                                        </div>
                                        {formData.emailEnabled && (
                                            <div className="ns-form-group full">
                                                <label>Modèle d’e-mail *</label>
                                                <select className="ns-input" value={formData.emailTemplateId}
                                                        onChange={e => setFormData({ ...formData, emailTemplateId: e.target.value ? Number(e.target.value) : '' })} required>
                                                    <option value="">Sélectionner un modèle actif...</option>
                                                    {emailTemplates.filter(template => template.isActive !== false).map(template => (
                                                        <option key={template.id} value={template.id}>{template.title} — {template.subject}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {modalStep === 2 && (
                                    <div className="ns-target-section">
                                        <div className="ns-target-cards">
                                            {Object.entries(TARGET_CONFIG).map(([key, config]) => {
                                                const Icon = config.icon;
                                                const isSelected = formData.targetTypes.includes(key);
                                                return (
                                                    <button type="button" key={key} className={`ns-target-card ${isSelected ? 'selected' : ''}`} onClick={() => toggleTargetType(key)}>
                                                        <div className="ns-target-card-icon"><Icon size={18} /></div>
                                                        <div className="ns-target-card-content">
                                                            <span className="ns-target-card-label">{config.label}</span>
                                                            <span className="ns-target-card-desc">{config.description}</span>
                                                        </div>
                                                        <div className="ns-target-card-check">{isSelected ? <CheckCircle2 size={18} /> : <Circle size={18} />}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {needsRolePicker && (
                                            <div className="ns-picker-block">
                                                <div className="ns-picker-header"><Shield size={14} /> <span>Rôles destinataires *</span></div>
                                                <Select isMulti options={roles.map(r => ({ value: r.id, label: r.name || r.roleName }))} value={roles.filter(r => formData.targetRoleIds.includes(r.id)).map(r => ({ value: r.id, label: r.name || r.roleName }))} onChange={sel => setFormData({ ...formData, targetRoleIds: sel ? sel.map(s => s.value) : [], targetUserIds: [] })} placeholder="Sélectionner..." />
                                            </div>
                                        )}
                                        {needsUserPicker && (
                                            <div className="ns-picker-block">
                                                <div className="ns-picker-header"><Users size={14} /> <span>Utilisateurs destinataires *</span></div>
                                                <Select isMulti options={availableUsersForSelect} value={availableUsersForSelect.filter(o => formData.targetUserIds.includes(o.value))} onChange={sel => setFormData({ ...formData, targetUserIds: sel ? sel.map(s => s.value) : [] })} placeholder="Sélectionner..." />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="ns-modal-footer">
                                {modalStep === 1 ? (
                                    <>
                                        <button type="button" className="ns-btn ns-btn-outline" onClick={() => setIsModalOpen(false)}>Annuler</button>
                                        <button type="button" className="ns-btn ns-btn-primary" onClick={() => setModalStep(2)} disabled={!formData.eventCode || !formData.inAppTitleTemplate || (formData.emailEnabled && !formData.emailTemplateId)}>Enregistrer</button>
                                    </>
                                ) : (
                                    <>
                                        <button type="button" className="ns-btn ns-btn-outline" onClick={() => setModalStep(1)}>← Retour</button>
                                        <button type="submit" className="ns-btn ns-btn-primary" disabled={!step2Complete}>{editingSetting ? 'Mettre à jour' : 'Créer'}</button>
                                    </>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <ScrollToTop />
        </div>
    );
};

export default NotificationSettings;
